// src/pages/ProfilePage.js
// Updated: 10/18/2025 11:58 PM

import React, { useState, useEffect } from 'react';
import Layout from '../components/common/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { db, storage } from '../firebase/config';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import toast from 'react-hot-toast';
import Spinner from '../components/common/Spinner';
import { FaUserCircle } from 'react-icons/fa';

// Helper function to normalize relationship IDs
const getId = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val.id) return val.id; // DocumentReference
  return '';
};

const ProfilePage = () => {
  const { userData, refreshUserData } = useAuth();
  const { familyData } = useFamily();

  // Form states (unchanged)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [gender, setGender] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);

  // States for family relationships (unchanged)
  const [familyMembers, setFamilyMembers] = useState([]);
  const [motherId, setMotherId] = useState('');
  const [fatherId, setFatherId] = useState('');
  const [spouseId, setSpouseId] = useState('');

  // Fetch all family members for dropdowns
  useEffect(() => {
    if (!familyData || !userData) return;

    const fetchFamilyMembers = async () => {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('familyId', '==', familyData.id));
      const querySnapshot = await getDocs(q);
      const members = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(member => member.id !== userData.uid) // Exclude self from lists
        // --- NEW SORTING LOGIC ---
        .sort((a, b) => {
          const lastNameA = a.lastName || '';
          const lastNameB = b.lastName || '';
          const firstNameA = a.firstName || '';
          const firstNameB = b.firstName || '';

          if (lastNameA.toLowerCase() < lastNameB.toLowerCase()) return -1;
          if (lastNameA.toLowerCase() > lastNameB.toLowerCase()) return 1;
          // If last names are the same, sort by first name
          if (firstNameA.toLowerCase() < firstNameB.toLowerCase()) return -1;
          if (firstNameA.toLowerCase() > firstNameB.toLowerCase()) return 1;
          return 0; // Names are identical
        });
        // --- END NEW SORTING LOGIC ---
      setFamilyMembers(members);
    };

    fetchFamilyMembers();
  }, [familyData, userData]);

  // Populate form with existing user data (unchanged)
  useEffect(() => {
    if (userData) {
      setFirstName(userData.firstName || '');
      setLastName(userData.lastName || '');
      setPhone(userData.phone || '');
      setAddress(userData.address || '');
      setGender(userData.gender || '');
      setPreviewUrl(userData.profilePictureUrl || '');
      setMotherId(getId(userData.motherId));
      setFatherId(getId(userData.fatherId));
      setSpouseId(getId(userData.spouseId));
    }
  }, [userData]);

  // handlePictureChange (unchanged)
  const handlePictureChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePicture(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // handleSubmit (unchanged)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!userData) {
      toast.error("You must be logged in to update your profile.");
      setLoading(false);
      return;
    }

    try {
      const userDocRef = doc(db, 'users', userData.uid);
      let profilePictureUrl = userData.profilePictureUrl || '';

      if (profilePicture) {
        const storageRef = ref(storage, `profilePictures/${userData.uid}/${profilePicture.name}`);
        const snapshot = await uploadBytes(storageRef, profilePicture);
        profilePictureUrl = await getDownloadURL(snapshot.ref);
      }

      const updatedData = {
        firstName,
        lastName,
        phone,
        address,
        gender,
        profilePictureUrl,
        motherId: motherId || null,
        fatherId: fatherId || null,
        spouseId: spouseId || null,
      };

      await updateDoc(userDocRef, updatedData);
      await refreshUserData();

      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error("Error updating profile: ", error);
      toast.error('Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  // JSX Return (unchanged structure, sorting happens before render)
  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b">
            <h1 className="text-2xl font-bold text-neutral-gray">My Profile</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your personal and family information.</p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              {/* Profile Picture Section */}
              <div className="flex items-center gap-4">
                {previewUrl ? <img src={previewUrl} alt="Profile" className="h-20 w-20 rounded-full object-cover" /> : <FaUserCircle className="h-20 w-20 text-gray-300" />}
                <div>
                  <label htmlFor="file-upload" className="cursor-pointer px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50">Change Picture</label>
                  <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handlePictureChange} accept="image/*" />
                </div>
              </div>

              {/* Personal Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name</label><input type="text" id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/></div>
                <div><label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label><input type="text" id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/></div>
                <div><label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number (Optional)</label><input type="tel" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/></div>
                <div><label htmlFor="address" className="block text-sm font-medium text-gray-700">Address (Optional)</label><input type="text" id="address" value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/></div>
                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700">Gender</label>
                  <select
                    id="gender"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              {/* Family Relationships Section */}
              <div className="pt-6 border-t">
                <h2 className="text-lg font-medium text-neutral-gray">Family Relationships</h2>
                <p className="text-sm text-gray-500 mt-1">Select family members already in the system. Use the Family Tree tools to add new relatives or establish links.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                  {/* Father Selector */}
                  <div>
                    <label htmlFor="fatherId" className="block text-sm font-medium text-gray-700">Father</label>
                    <select id="fatherId" value={fatherId} onChange={(e) => setFatherId(e.target.value)} className="mt-1 w-full border-gray-300 rounded-md shadow-sm">
                      <option value="">Select a family member</option>
                      {/* Options are now pre-sorted */}
                      {familyMembers.filter(m => m.gender === 'Male').map(member => (
                        <option key={member.id} value={member.id}>{member.firstName} {member.lastName}</option>
                      ))}
                    </select>
                  </div>
                  {/* Mother Selector */}
                  <div>
                    <label htmlFor="motherId" className="block text-sm font-medium text-gray-700">Mother</label>
                    <select id="motherId" value={motherId} onChange={(e) => setMotherId(e.target.value)} className="mt-1 w-full border-gray-300 rounded-md shadow-sm">
                      <option value="">Select a family member</option>
                       {/* Options are now pre-sorted */}
                      {familyMembers.filter(m => m.gender === 'Female').map(member => (
                        <option key={member.id} value={member.id}>{member.firstName} {member.lastName}</option>
                      ))}
                    </select>
                  </div>
                  {/* Spouse Selector */}
                  <div>
                    <label htmlFor="spouseId" className="block text-sm font-medium text-gray-700">Spouse</label>
                    <select id="spouseId" value={spouseId} onChange={(e) => setSpouseId(e.target.value)} className="mt-1 w-full border-gray-300 rounded-md shadow-sm">
                      <option value="">Select a family member</option>
                      {/* Options are now pre-sorted */}
                      {familyMembers.map(member => (<option key={member.id} value={member.id}>{member.firstName} {member.lastName}</option>))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            {/* Save Button */}
            <div className="p-6 bg-gray-50 rounded-b-lg flex justify-end">
              <button type="submit" disabled={loading} className="px-6 py-2 bg-primary text-white rounded-md hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center">
                {loading ? <Spinner size="sm" /> : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;